import React from 'react';
import { Box, Typography } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { useTokens } from 'theme';
import { fmtNumber } from './format';
import { formatNumber } from 'i18n/format';

import { useTranslation } from 'react-i18next';

export function StatCard({ icon, label, value, sub, accentColor }) {
  useTranslation();
  const themeTokens = useTokens();
  return (
    <Box className="nb-stat" sx={{ height: '100%', minWidth: 0, position: 'relative', overflow: 'hidden', overflowWrap: 'anywhere' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: `${accentColor}${themeTokens.mode === 'dark' ? '2E' : '1F'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accentColor,
            '& svg': { fontSize: 18 },
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, mb: 1 }}>
        {value ?? '—'}
      </Typography>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: themeTokens.inkMuted }}>
        {label}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: themeTokens.inkMuted, mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}

export default function StatCards({ usage }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const tokens = usage?.tokens ?? {};
  const queries = usage?.queries ?? {};
  const byChannel = usage?.by_channel ?? {};

  const cards = [
    {
      icon: <KeyIcon fontSize="small" />,
      label: t('data.activeTokens'),
      value: fmtNumber(tokens.active ?? 0),
      sub: t('data.totalCount', { amount: fmtNumber(tokens.total ?? 0) }),
      accentColor: themeTokens.positive,
    },
    {
      icon: <QueryStatsIcon fontSize="small" />,
      label: t('data.totalQueries'),
      value: fmtNumber(queries.total ?? 0),
      sub: t('data.valueIn7d', { value1: fmtNumber(queries.last_7d ?? 0) }),
      accentColor: themeTokens.series[0],
    },
    {
      icon: <BoltIcon fontSize="small" />,
      label: t('data.last24h'),
      value: fmtNumber(queries.last_24h ?? 0),
      sub: t('data.queries'),
      accentColor: themeTokens.info,
    },
    {
      icon: <CheckCircleIcon fontSize="small" />,
      label: t('data.successRate'),
      value: queries.success_rate === null || queries.success_rate === undefined ? '—' : formatNumber(queries.success_rate / 100, { style: 'percent', maximumFractionDigits: 1 }),
      sub: t('data.errorCount', { amount: fmtNumber(queries.error ?? 0) }),
      accentColor: themeTokens.series[2],
    },
    {
      icon: <SpeedIcon fontSize="small" />,
      label: t('data.avgLatency'),
      value: queries.avg_duration_ms === null || queries.avg_duration_ms === undefined ? '—' : t('data.milliseconds', { amount: fmtNumber(queries.avg_duration_ms) }),
      sub: t('data.perQuery'),
      accentColor: themeTokens.warning,
    },
    {
      icon: <ViewInArIcon fontSize="small" />,
      label: 'Evolve Cube',
      value: fmtNumber(byChannel.cube ?? 0),
      sub: 'queries',
      accentColor: themeTokens.negative,
    },
  ];

  return (
    <Box sx={{
      display: 'grid', width: '100%', minWidth: 0, gap: 2,
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
    }}>
      {cards.map((card) => <StatCard key={card.label} {...card} />)}
    </Box>
  );
}
